import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import GurujiChatWidget from '../components/GurujiChatWidget';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight, BookOpen, TrendingUp } from 'lucide-react';
import { getExamsByCategory, getExamById } from '../data/examData';

const Exams = () => {
  const categories = getExamsByCategory();

  const getCategoryColor = (category) => {
    const colors = {
      'RBI & NABARD': 'from-red-600 to-red-700',
      'SSC Exams': 'from-blue-600 to-blue-700',
      'Bank PO': 'from-green-600 to-green-700',
      'Bank Clerk': 'from-purple-600 to-purple-700'
    };
    return colors[category] || 'from-gray-600 to-gray-700';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <GurujiChatWidget />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Complete Exam Information Hub
            </h1>
            <p className="text-xl opacity-90 mb-8">
              Get comprehensive details about all major competitive exams - notifications, syllabus, salary, preparation strategy & more
            </p>
            <div className="flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                <span>14 Exams Covered</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                <span>Updated Content</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Exams by Category */}
      <div className="container mx-auto px-4 py-16">
        <div className="space-y-12">
          {Object.entries(categories).map(([category, examIds]) => (
            <div key={category}>
              <div className={`bg-gradient-to-r ${getCategoryColor(category)} text-white rounded-t-xl p-6`}>
                <h2 className="text-3xl font-bold">{category}</h2>
                <p className="opacity-90 mt-2">{examIds.length} exams available</p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                {examIds.map((examId) => {
                  const exam = getExamById(examId);
                  if (!exam) return null;

                  return (
                    <Card key={examId} className="hover:shadow-xl transition-shadow duration-300 border-2 hover:border-red-600">
                      <CardHeader>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-5xl">{exam.icon}</span>
                          <div>
                            <CardTitle className="text-xl">{exam.name}</CardTitle>
                            <p className="text-sm text-gray-600 mt-1">{exam.fullName}</p>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3 mb-4">
                          {exam.overview && (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Conducted By:</span>
                                <span className="font-semibold text-gray-800">{exam.overview.conductedBy?.split(' ').slice(0, 3).join(' ')}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Level:</span>
                                <span className="font-semibold text-gray-800">{exam.overview.level}</span>
                              </div>
                            </>
                          )}
                          {exam.salary && exam.salary.totalCTC && (
                            <div className="flex justify-between text-sm bg-green-50 p-2 rounded">
                              <span className="text-gray-600">Salary:</span>
                              <span className="font-bold text-green-700">{exam.salary.totalCTC || exam.salary.range}</span>
                            </div>
                          )}
                          {exam.vacancy && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Vacancies:</span>
                              <span className="font-semibold text-blue-700">{exam.vacancy.expected}</span>
                            </div>
                          )}
                        </div>
                        
                        <Link to={`/exams/${examId}`}>
                          <Button className="w-full bg-red-600 hover:bg-red-700 text-white">
                            View Complete Details
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl p-12 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Exam Preparation?</h2>
          <p className="text-lg mb-8 opacity-90">Join Vidya365 for comprehensive test series, study materials, and expert guidance</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/student/register">
              <Button className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Register for Test Series
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Exams;
